'use client'

import React, { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Alert, AlertDescription } from '../ui/alert'
import { Sparkles, Building, Globe, FileText, CheckCircle, ArrowRight, Wand2 } from 'lucide-react'
import { Badge } from '../ui/badge'
import { mockBrandService, Brand } from '../../utils/mockData'

interface CreateBrandModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (brand: Brand) => void
}

export function CreateBrandModal({ isOpen, onClose, onSuccess }: CreateBrandModalProps) {
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    targetAudience: '',
    goals: [] as string[]
  })
  const [aiAnalysis, setAiAnalysis] = useState<any>(null)

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleClose = () => {
    setStep(1)
    setFormData({
      name: '',
      description: '',
      website: '',
      industry: '',
      targetAudience: '',
      goals: []
    })
    setAiAnalysis(null)
    setError(null)
    onClose()
  }

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      // Simulate AI analysis
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const mockAnalysis = {
        websiteAnalyzed: !!formData.website,
        elementsFound: formData.website ? Math.floor(Math.random() * 15) + 5 : 0,
        suggestedIndustry: formData.industry || 'Technology',
        suggestedAudience: formData.targetAudience || 'Business professionals',
        confidence: 0.85
      }
      
      setAiAnalysis(mockAnalysis)
      setStep(2)
    } catch (error: any) {
      setError(error.message || 'Failed to analyze brand information')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFinalSubmit = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const { brand, error } = await mockBrandService.createBrand({
        name: formData.name,
        description: formData.description,
        website_url: formData.website
      })
      
      if (error) {
        throw new Error(error)
      }

      if (brand) {
        onSuccess(brand)
        handleClose()
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create brand')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <DialogTitle>
                {step === 1 ? 'Create New Brand' : 'AI Analysis Complete'}
              </DialogTitle>
              <DialogDescription>
                {step === 1 
                  ? 'Let our AI analyze your brand and create guidelines automatically'
                  : 'Review the analysis and create your brand'
                }
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="space-y-6">
            {/* Step indicator */}
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</div>
              <span>Brand Information</span>
              <div className="w-8 h-px bg-slate-300"></div>
              <div className="w-6 h-6 bg-slate-200 text-slate-400 rounded-full flex items-center justify-center text-xs font-medium">2</div>
              <span className="text-slate-400">AI Analysis</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Brand Name *</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your brand name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Brand Description</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    id="description"
                    placeholder="Briefly describe what your brand does..."
                    value={formData.description}
                    onChange={handleInputChange('description')}
                    className="pl-10 resize-none"
                    rows={3}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website URL (Optional)</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://yourbrand.com"
                    value={formData.website}
                    onChange={handleInputChange('website')}
                    className="pl-10"
                  />
                </div>
                {formData.website && (
                  <div className="flex items-center space-x-2 text-xs text-blue-600">
                    <Wand2 className="w-3 h-3" />
                    <span>AI will analyze your website for brand elements</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-800">Smart AI Analysis</p>
                  <p className="text-xs text-blue-600">
                    Our AI will analyze your website and existing materials to automatically generate 
                    brand guidelines, saving you hours of work.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Wand2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Analyze with AI
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        {step === 2 && aiAnalysis && (
          <div className="space-y-6">
            {/* Step indicator */}
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs">
                <CheckCircle className="w-3 h-3" />
              </div>
              <span className="text-green-600">Brand Information</span>
              <div className="w-8 h-px bg-green-500"></div>
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">2</div>
              <span>AI Analysis</span>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Analysis Complete!</span>
                </div>
                <div className="space-y-2 text-sm">
                  {aiAnalysis.websiteAnalyzed && (
                    <div className="flex items-center space-x-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Website analyzed - {aiAnalysis.elementsFound} brand elements found</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>Brand guidelines pre-populated</span>
                  </div>
                  <div className="flex items-center space-x-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>AI confidence: {Math.round(aiAnalysis.confidence * 100)}%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">Detected Information:</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">Brand Name</span>
                    <Badge variant="secondary">{formData.name}</Badge>
                  </div>
                  {formData.description && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">Description</span>
                      <span className="text-xs text-slate-500 max-w-xs truncate">{formData.description}</span>
                    </div>
                  )}
                  {aiAnalysis.suggestedIndustry && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">Industry</span>
                      <Badge variant="outline">{aiAnalysis.suggestedIndustry}</Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-800">What's Next?</p>
                  <p className="text-xs text-blue-600">
                    Your brand will be created with AI-generated guidelines. You can refine and 
                    customize everything in the brand dashboard.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                onClick={handleFinalSubmit}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={isLoading}
              >
                {isLoading ? 'Creating Brand...' : 'Create Brand'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}